import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  #signedOutLinksTimer;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
    this.#hideSignedOutQuickLinks();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
    window.clearInterval(this.#signedOutLinksTimer);
  }

  #hideSignedOutQuickLinks = () => {
    const account = this.querySelector('shopify-account');
    if (!account) return;

    let attempts = 0;
    const installStyles = () => {
      attempts += 1;
      const shadowRoot = account.shadowRoot;

      if (shadowRoot && !shadowRoot.querySelector('#qualuca-hide-signed-out-quick-links')) {
        const style = document.createElement('style');
        style.id = 'qualuca-hide-signed-out-quick-links';
        style.textContent = '.account__menu-region { display: none !important; }';
        shadowRoot.append(style);
      }

      if (shadowRoot || attempts >= 100) {
        window.clearInterval(this.#signedOutLinksTimer);
      }

      return Boolean(shadowRoot);
    };

    if (!installStyles()) {
      this.#signedOutLinksTimer = window.setInterval(installStyles, 50);
    }
  };

  #closeAccountMenu = () => {
    const menu = this.querySelector('.qualuca-account-menu');
    if (menu?.open) menu.open = false;
  };

  #onDocumentClick = (event) => {
    const menu = this.querySelector('.qualuca-account-menu');
    if (menu?.open && !menu.contains(event.target)) this.#closeAccountMenu();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#closeAccountMenu();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
